// Modern-design overlay primitives — production port from
// `storybook/src/prototypes/modern-design/components/primitives.tsx`.
// Tokens come from `ui-vite/src/styles/document-overlay-tokens.css`.

import type React from 'react'
import { useEffect, useRef } from 'react'

export { type SegmentedTab, SegmentedTabs } from '@/components/ui/segmented-tabs'

// ─── Overlay Backdrop ────────────────────────────────────────────────────────
//
// Fixed-position dim layer that centers its children, locks body scroll,
// closes on Esc + outside-click, and traps Tab focus inside the dialog.
// Restores focus to the previously-focused element on unmount.

export function OverlayBackdrop({
  onClose,
  children,
}: {
  onClose: () => void
  children: React.ReactNode
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const focusables = wrapper.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    focusables[0]?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const list = wrapper.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (list.length === 0) return
      const first = list[0]
      const last = list[list.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !wrapper.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    wrapper.addEventListener('keydown', onKey)
    return () => {
      wrapper.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
  }, [])

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      ref={wrapperRef}
      // A layout-only wrapper. It must not be `aria-hidden`: the dialog is its
      // child, and hiding the wrapper hides the dialog from assistive tech too.
      role="presentation"
      onMouseDown={onBackdropMouseDown}
      // Phone: the dialog is edge to edge, so the backdrop keeps no margin around it.
      className="p-0 md:p-6"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'var(--overlay-backdrop)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  )
}
